import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import PasoPrecios from '../facturacionRemitos/PasoPrecios';
import PasoConfirmacion from '../facturacionRemitos/PasoConfirmacion';
import { preparar, facturar } from '../facturacionRemitos/functionsFacturacion';
import { UrlApi } from '../../../../../services/apirest';

const steps = ['Ingresar precios', 'Confirmar facturación'];

const ModalFacturacion = ({ 
  open, 
  onClose, 
  pedidosIds, 
  onSuccess, 
  renglonesCompletos,
  cliente,
  pedidos,
  suc,
  unidad 
}) => {
  // console.log("renglonesCompletos",renglonesCompletos);
  
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [productosFacturar, setProductosFacturar] = useState([]);
  const [totales, setTotales] = useState({
    subtotal: 0,
    iva: 0,
    total: 0
  });
  const [facturaData, setFacturaData] = useState(null);
  const [respuestaFactura, setRespuestaFactura] = useState(null);

  const numerosRemito = useMemo(() => {
    if (!pedidos || !pedidosIds) return '';
    return pedidos
      .filter(pedido => pedidosIds.includes(pedido.id))
      .map(pedido => pedido.remito_entrega)
      .join('|');
  }, [pedidos, pedidosIds]);

  useEffect(() => {
    console.log('Números de remito a facturar:', numerosRemito);
  }, [numerosRemito]);

  // Agrupar renglones por producto y sumar cantidades
  useEffect(() => {
    if (open && renglonesCompletos && pedidosIds && pedidosIds.length > 0) {
      const renglonesFiltrados = renglonesCompletos.filter(
        renglon => pedidosIds.includes(renglon.id_pedido)
      );

      const productosAgrupados = renglonesFiltrados.reduce((acc, renglon) => {
        const key = renglon.nombre_producto;
        
        if (!acc[key]) {
          acc[key] = {
            id_producto: renglon.producto,
            nombre: renglon.nombre_producto,
            cantidad_total: 0,
            valor_facturacion_unitario: renglon.valor_facturacion ? parseFloat(renglon.valor_facturacion) : 0,
            valor_iva_unitario: renglon.valor_iva ? parseFloat(renglon.valor_iva) : 0,
            pedidos: new Set(),
            detalle_factura: renglon.detalle_factura || renglon.nombre_producto
          };
        }
        
        acc[key].cantidad_total += renglon.cantidad || 0;
        if (renglon.id_pedido) {
          acc[key].pedidos.add(renglon.id_pedido);
        }
        
        return acc;
      }, {});

      const productosArray = Object.values(productosAgrupados).map(prod => ({
        ...prod,
        pedidos_count: prod.pedidos.size,
        precio_unitario_con_iva: 0
      }));

      setProductosFacturar(productosArray);
      setActiveStep(0);
      setFacturaData(null);
      setRespuestaFactura(null);
    }
  }, [open, renglonesCompletos, pedidosIds]);

// Calcular totales
const calcularTotales = (productos) => {
  let subtotalAcumulado = 0;
  let ivaAcumulado = 0;
  let totalAcumulado = 0;

  productos.forEach((producto) => {
    const cantidad = producto.cantidad_total || 0;
    const precioConIva = producto.precio_unitario_con_iva || 0;
    
    const totalRenglon = cantidad * precioConIva;
    
    // Obtener porcentaje de IVA (valor_facturacion_unitario es el porcentaje)
    const porcentajeIva = producto.valor_facturacion_unitario || 0;
    
    // Calcular subtotal y IVA correctamente
    const subtotalRenglon = totalRenglon / (1 + porcentajeIva / 100);
    const ivaRenglon = totalRenglon - subtotalRenglon;

    subtotalAcumulado += subtotalRenglon;
    ivaAcumulado += ivaRenglon;
    totalAcumulado += totalRenglon;
  });

  return {
    subtotal: subtotalAcumulado,
    iva: ivaAcumulado,
    total: totalAcumulado
  };
};

  const handlePreciosChange = (productosActualizados) => {
    setProductosFacturar(productosActualizados);
    const nuevosTotales = calcularTotales(productosActualizados);
    setTotales(nuevosTotales);
  };

const handleContinue = (productosConPrecios) => {
  const productosParaFacturar = productosConPrecios.map(prod => {
    const cantidad = prod.cantidad_total;
    const precioConIva = prod.precio_unitario_con_iva;
    const totalRenglon = cantidad * precioConIva;
    
    // Obtener porcentaje de IVA
    const porcentajeIva = prod.valor_facturacion_unitario || 0;
    
    // Calcular subtotal y IVA correctamente
    const subtotalRenglon = totalRenglon / (1 + porcentajeIva / 100);
    const ivaRenglon = totalRenglon - subtotalRenglon;

    return {
      id_producto: prod.id_producto,
      nombre: prod.nombre,
      cantidad: cantidad,
      precio_unitario: precioConIva,
      valor_facturacion_original: prod.valor_facturacion_unitario,
      valor_iva_original: prod.valor_iva_unitario,
      subtotal: subtotalRenglon,
      iva: ivaRenglon,
      total: totalRenglon,
      detalle_factura: prod.detalle_factura
    };
  });
  

  setFacturaData({
    productos: productosParaFacturar,
    totales: totales
  });
  setActiveStep(1);
};

  const handleBack = () => {
    setActiveStep(0);
    setRespuestaFactura(null);
  };

  const agruparRenglonesPorProducto = (renglones, mapaPrecios) => {
  const agrupados = {};
  
  renglones.forEach(renglon => {
    const key = renglon.producto;
    
    if (!agrupados[key]) {
      agrupados[key] = {
        id: renglon.id,
        uuid_pedido: renglon.uuid_pedido,
        producto: renglon.producto,
        producto_detalle_factura: renglon.nombre_producto,
        cantidad: 0,
        especificaciones: renglon.especificaciones,
        precio: mapaPrecios[renglon.producto] || renglon.precio,
        usuario: renglon.usuario,
        created_at: renglon.created_at,
        updated_at: renglon.updated_at,
        activo: renglon.activo,
        promo: renglon.promo,
        entrega_sena: renglon.entrega_sena,
        valor_envase: renglon.valor_envase,
        iva: renglon.valor_facturacion,
        iva_cod_afip: renglon.iva_cod_afip,
        pedidos_origen: []
      };
    }
    
    agrupados[key].cantidad += renglon.cantidad || 0;
    
    if (renglon.id_pedido && !agrupados[key].pedidos_origen.includes(renglon.id_pedido)) {
      agrupados[key].pedidos_origen.push(renglon.id_pedido);
    }
  });
  
  return Object.values(agrupados);
};

const handleSubmit = async () => {
  setLoading(true);
  
  try {
    // Crear mapa de precios
    const mapaPrecios = {};
    productosFacturar.forEach(prod => {
      mapaPrecios[prod.id_producto] = prod.precio_unitario_con_iva;
    });

    // Agrupar renglones
    const renglonesAgrupados = agruparRenglonesPorProducto(renglonesCompletos, mapaPrecios);
    

    let bodyTemp = {};
    
    const setBody = (body) => {
      bodyTemp = body;
    };
    
    preparar(
      cliente,
      pedidos,
      suc,
      unidad,
      setBody,
      () => {},
      renglonesAgrupados,
      numerosRemito
    );
    
    const setRespuesta = (respuesta) => {
      setRespuestaFactura(respuesta);
      
      // Si la facturación fue exitosa, actualizar los renglones
      if (respuesta.title === 'Facturación exitosa.') {
        // Llamar a la función de actualización por producto
        actualizarRenglonesFacturados();
      }
    };

    await facturar(bodyTemp, setRespuesta);
    
  } catch (error) {
    console.error('Error en facturación:', error);
    setRespuestaFactura({
      open: true,
      title: "Error",
      message: "Error interno del servidor.",
      color: "#ff0201",
      icon: null
    });
  } finally {
    setLoading(false);
  }
};
  
const actualizarRenglonesFacturados = async () => {
  try {
    // Agrupar renglones por producto
    const renglonesPorProducto = {};
    
    productosFacturar.forEach(producto => {
      // Encontrar todos los renglones de este producto
      const renglonesDelProducto = renglonesCompletos
        .filter(r => pedidosIds.includes(r.id_pedido) && r.producto === producto.id_producto)
        .map(r => r.id);
      
      if (renglonesDelProducto.length > 0) {
        renglonesPorProducto[producto.id_producto] = {
          id_producto: producto.id_producto,
          precio: producto.precio_unitario_con_iva,
          renglones_ids: renglonesDelProducto
        };
      }
    });


    // Preparar el array de productos para enviar
    const productosParaActualizar = Object.values(renglonesPorProducto);

    if (productosParaActualizar.length === 0) {
      console.log("No hay productos para actualizar");
      return;
    }

    // Hacer la petición PUT
    const response = await fetch(`${UrlApi}/envasado_pedidos_renglon/update-by-product`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        productos: productosParaActualizar 
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error en actualización masiva');
    }

    const result = await response.json();
    // console.log('Actualización exitosa:', result);
    
    // Mostrar mensaje de éxito y cerrar
    setTimeout(() => {
      onSuccess();
      handleClose();
    }, 2000);

  } catch (error) {
    console.error('Error actualizando renglones:', error);
    setRespuestaFactura({
      open: true,
      title: "Error",
      message: "Error al actualizar los precios de los renglones.",
      color: "#ff0201",
      icon: null
    });
  }
};

  const handleClose = () => {
    setActiveStep(0);
    setProductosFacturar([]);
    setFacturaData(null);
    setRespuestaFactura(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Facturar Pedidos
              </Typography>
              <Stepper activeStep={activeStep} sx={{ mt: 1, minWidth: 300 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {activeStep === 0 ? (
        <PasoPrecios
          productosFacturar={productosFacturar}
          totales={totales}
          pedidosIds={pedidosIds}
          pedidos={pedidos}
          loading={loading}
          onPreciosChange={handlePreciosChange}
          onContinue={handleContinue}
          onClose={handleClose}
        />
      ) : (
        <PasoConfirmacion
          facturaData={facturaData}
          respuestaFactura={respuestaFactura}
          cliente={cliente}
          pedidosIds={pedidosIds}
          loading={loading}
          onBack={handleBack}
          onSubmit={handleSubmit}
          onClose={handleClose}
          pedidos={pedidos}
          renglonesCompletos={renglonesCompletos}
        />
      )}
    </Dialog>
  );
};

export default ModalFacturacion;